'use client';

import classNames from 'classnames';
import { ReactNode } from 'react';
import Typography from '@alltrails/core/components/Typography';
import useShowMoreLinks from '@alltrails/layout/hooks/useShowMoreLinks';
import type { SeoLinkItem } from '@alltrails/types/SeoLinkItem';
import LinksColumn from '../LinksColumn';
import styles from './MinimizedSeoLinksGrid.module.scss';

const MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT = 15;
const COLLAPSED_FIRST_COLUMN_SIZE = Math.ceil(MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT / 2);

type MinimizedSeoLinksGridSharedProps = {
  linkItems: SeoLinkItem[];
  canRenderAsTwoColumns?: boolean;
  isBot?: boolean;
  title: string | ReactNode;
  seoTestAddTrailsToLocationLinks?: boolean;
  seoTestAddHikingTrailsToLocationLinks?: boolean;
  containerClassName?: string;
};

export default function MinimizedSeoLinksGrid({
  linkItems,
  canRenderAsTwoColumns = true,
  isBot = false,
  title,
  containerClassName,
  seoTestAddTrailsToLocationLinks,
  seoTestAddHikingTrailsToLocationLinks
}: MinimizedSeoLinksGridSharedProps) {
  const totalItems = linkItems.length;
  const collapsedLimit = canRenderAsTwoColumns ? MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT : COLLAPSED_FIRST_COLUMN_SIZE;
  const { visibleLinkItems, showMoreSlot, isExpanded } = useShowMoreLinks({
    linkItems,
    collapsedLimit,
    isBot,
    showMoreToggleClassName: styles.showMoreToggle
  });

  const isCollapsed = showMoreSlot && !isExpanded;

  let firstColumnSize = canRenderAsTwoColumns ? Math.ceil(totalItems / 2) : totalItems;
  if (isCollapsed) {
    firstColumnSize = COLLAPSED_FIRST_COLUMN_SIZE;
  }

  const visibleFirstBatch = visibleLinkItems.slice(0, firstColumnSize);
  const visibleSecondBatch = visibleLinkItems.slice(firstColumnSize, totalItems);

  let showToggleInFirstColumn;
  if (canRenderAsTwoColumns) {
    showToggleInFirstColumn = visibleSecondBatch.length === 0 && visibleFirstBatch.length > MINIMIZED_SEO_LINKS_GRID_COLLAPSED_COUNT;
  } else {
    showToggleInFirstColumn = !!showMoreSlot;
  }

  return (
    <div className={classNames(styles.container, containerClassName)}>
      {title && (
        <Typography variant="heading200" component="h3" mt="0" className={styles.title}>
          {title}
        </Typography>
      )}
      <div className={styles.linkBlockColumns}>
        <div className={styles.linkColumn}>
          <LinksColumn
            links={visibleFirstBatch}
            seoTestAddTrailsToLocationLinks={seoTestAddTrailsToLocationLinks}
            seoTestAddHikingTrailsToLocationLinks={seoTestAddHikingTrailsToLocationLinks}
          />
          {showToggleInFirstColumn && showMoreSlot}
        </div>
        {!!visibleSecondBatch.length && canRenderAsTwoColumns && (
          <div className={styles.linkColumn}>
            <LinksColumn
              links={visibleSecondBatch}
              seoTestAddTrailsToLocationLinks={seoTestAddTrailsToLocationLinks}
              seoTestAddHikingTrailsToLocationLinks={seoTestAddHikingTrailsToLocationLinks}
            />
            {!showToggleInFirstColumn && showMoreSlot}
          </div>
        )}
      </div>
    </div>
  );
}
